import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Send, Sparkles, Loader2, Flag, ShieldBan, MoreVertical, Check, CheckCheck, Clock, Heart, MapPin, Lightbulb, Video, Phone, PhoneOff, History, Paperclip, Bookmark, BookmarkCheck, RefreshCw, Wand2, Trash2, UserX, Eraser } from 'lucide-react';
import VerifiedBadge from '@/components/VerifiedBadge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import VoiceRecorder from '@/components/VoiceRecorder';
import VoiceBubble from '@/components/VoiceBubble';
import DemoProfileBadge from '@/components/DemoProfileBadge';
import CallModal from '@/components/CallModal';
import CallHistoryDialog from '@/components/CallHistoryDialog';
import { useLifecycleController } from '@/components/lifecycle/LifecycleUI';
import { ProposalComposer, ViewDate } from '@/components/lifecycle/ProposalComposer';

const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : process.env.REACT_APP_BACKEND_URL;
function getPhotoUrl(p) { if (!p) return null; if (p.startsWith('http')) return p; return `${BACKEND_URL}/api/files/${p}`; }

function isSameDay(a, b) {
  if (!a || !b) return false;
  const da = new Date(a), db = new Date(b);
  return da.getFullYear() === db.getFullYear()
    && da.getMonth() === db.getMonth()
    && da.getDate() === db.getDate();
}

function formatDateDivider(iso) {
  const d = new Date(iso);
  const now = new Date();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (isSameDay(d, now)) return 'Today';
  if (isSameDay(d, yesterday)) return 'Yesterday';
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'long' });
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Chat() {
  const { matchId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // Feb 26 2026 · Date Planner preset support. When we arrive with
  // `?preset=ideas_for_next_date` (from the mutual We-Met celebration hint)
  // OR `?preset=ideas_for_first_date` (from the mutual Meet hint), auto-open
  // the Planner tuned to that preset. Also lets the mobile app deep-link.
  const [datePlannerPreset, setDatePlannerPreset] = useState(
    searchParams.get('preset') || 'ideas_for_first_date'
  );
  const navigate = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [lifecycle, setLifecycle] = useState(null);            // Match → Meet → Keep projection
  const [text, setText] = useState('');
  const [icebreakers, setIcebreakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadingIce, setLoadingIce] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const [progress, setProgress] = useState(null);
  const [dateIdeas, setDateIdeas] = useState([]);
  // AI Date Planner (VIP-only) — track loading + 403 upgrade state separately.
  const [dateIdeasLoading, setDateIdeasLoading] = useState(false);
  const [dateIdeasVipGate, setDateIdeasVipGate] = useState(false);
  const [savedIdeaTitles, setSavedIdeaTitles] = useState(new Set());
  const toggleSaveIdea = async (idea) => {
    const isSaved = savedIdeaTitles.has(idea.title);
    try {
      if (isSaved) {
        await api.delete('/date-ideas/saved', {
          params: { match_id: matchId, title: idea.title },
        });
        setSavedIdeaTitles((s) => { const n = new Set(s); n.delete(idea.title); return n; });
        toast.success('Removed from saved');
      } else {
        await api.post('/date-ideas/save', {
          match_id: matchId,
          title: idea.title,
          description: idea.description,
          category: idea.category,
        });
        setSavedIdeaTitles((s) => new Set([...s, idea.title]));
        toast.success('Saved for later');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update');
    }
  };
  const fetchDateIdeas = async (preset = datePlannerPreset) => {
    setDateIdeasLoading(true);
    try {
      const r = await api.get(`/date-ideas/${matchId}`, { params: { preset } });
      setDateIdeas(r.data?.ideas || []);
      setDateIdeasVipGate(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setDateIdeasVipGate(true);
      } else {
        toast.error(err.response?.data?.detail || 'Could not load ideas');
      }
    } finally {
      setDateIdeasLoading(false);
    }
  };
  const openDatePlanner = async (presetOverride) => {
    // If a preset override is passed (e.g., from the mutual We-Met toast CTA)
    // we swap presets AND clear cached ideas so the Planner refreshes with
    // the correct tone (first-date vs next-date).
    const nextPreset = presetOverride || datePlannerPreset;
    const presetChanged = nextPreset !== datePlannerPreset;
    if (presetChanged) {
      setDatePlannerPreset(nextPreset);
      setDateIdeas([]);
    }
    setShowIdeas(true);
    setDateIdeasVipGate(false);
    // Refresh saved titles every open so the bookmark states are accurate.
    try {
      const r = await api.get('/date-ideas/saved', { params: { match_id: matchId } });
      setSavedIdeaTitles(new Set((r.data?.ideas || []).map((i) => i.title)));
    } catch { /* non-fatal */ }
    if (dateIdeas.length > 0 && !presetChanged) return;
    await fetchDateIdeas(nextPreset);
  };
  const refreshDateIdeas = async () => {
    // Clear cached ideas so the LLM produces a fresh batch tailored on the
    // same match. Bookmarks remain intact (persisted server-side).
    setDateIdeas([]);
    await fetchDateIdeas();
  };
  const [showIdeas, setShowIdeas] = useState(false);
  const [myTier, setMyTier] = useState('free');
  const [reactionMsgId, setReactionMsgId] = useState(null);
  // Video/voice call state — see calls.py + CallModal.jsx.
  const [callEnabled, setCallEnabled] = useState(false);   // toggled by GET /calls/status
  const [callState, setCallState] = useState(null);        // null | 'outgoing' | 'incoming' | 'live'
  const [callMode, setCallMode] = useState('video');       // 'video' | 'voice'
  const [inviteId, setInviteId] = useState(null);          // shared id between caller + callee
  const [callToken, setCallToken] = useState(null);
  const [callRoomName, setCallRoomName] = useState(null);
  const outgoingTimeoutRef = useRef(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [compatOpen, setCompatOpen] = useState(false);
  const [compatData, setCompatData] = useState(null);
  const [compatLoading, setCompatLoading] = useState(false);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeout = useRef(null);
  // Refs for call state that the WS `onmessage` closure needs to read fresh.
  const inviteIdRef = useRef(null);
  useEffect(() => { inviteIdRef.current = inviteId; }, [inviteId]);

  // Feb 26 2026 · If we arrive with a `?preset=` query param (from a mutual
  // Meet/We-Met celebration hint on either web or mobile), auto-open the
  // Date Planner with the matching preset once, then strip the param so a
  // back-swipe/refresh doesn't re-fire the dialog.
  const presetHandledRef = useRef(false);
  useEffect(() => {
    if (presetHandledRef.current) return;
    const p = searchParams.get('preset');
    if (!p || !matchId) return;
    presetHandledRef.current = true;
    openDatePlanner(p);
    // Strip preset from URL after opening.
    const next = new URLSearchParams(searchParams);
    next.delete('preset');
    setSearchParams(next, { replace: true });
  }, [matchId]);

  // Feb 26 2026 · Proposal composer + view-date sheet state.
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerMode, setComposerMode] = useState('propose');   // 'propose' | 'counter'
  const [composerInitial, setComposerInitial] = useState(null);
  const [viewDateOpen, setViewDateOpen] = useState(false);

  // Feb 2026 · Match → Meet → Keep lifecycle controller. Composes the header
  // pill + bottom sheet + celebration takeover; dispatches the lifecycle
  // endpoints and refreshes local state on success.
  const lifecycleCtl = useLifecycleController({
    matchId,
    lifecycle,
    otherName: otherUser?.display_name,
    onLifecycleUpdate: setLifecycle,
    onOpenDatePlanner: (preset) => openDatePlanner(preset),
    onOpenProposalComposer: (initial, mode) => {
      setComposerInitial(initial);
      setComposerMode(mode || 'propose');
      setComposerOpen(true);
    },
    onOpenViewDate: () => setViewDateOpen(true),
  });

  const fetchChat = useCallback(async () => {
    try {
      const r = await api.get(`/chat/${matchId}`);
      setMessages(r.data.messages); setOtherUser(r.data.other_user); setMatchData(r.data.match);
      setLifecycle(r.data.lifecycle || null);
      setMyTier(r.data.my_tier || 'free');
      // Mark as read
      api.put(`/chat/${matchId}/read`).catch(() => {});
    } catch { navigate('/matches', { replace: true }); }
    finally { setLoading(false); }
  }, [matchId, navigate]);

  const connectWS = useCallback(() => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/chat/${matchId}?token=${token}`);
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.type === 'typing') {
        if (data.sender_id !== user?.user_id) {
          setOtherTyping(data.is_typing);
          if (data.is_typing) setTimeout(() => setOtherTyping(false), 4000);
        }
      } else if (data.type === 'read_receipt') {
        setMessages(prev => prev.map(m => (m.sender_id === user?.user_id ? ({
          ...m,
          read_by: Array.from(new Set([...(m.read_by || []), data.reader_id])),
          read_at: { ...(m.read_at || {}), [data.reader_id]: data.read_at }
        }) : m)));
      } else if (data.type === 'reaction') {
        setMessages(prev => prev.map(m => m.message_id === data.message_id ? { ...m, reactions: data.reactions } : m));
      } else if (data.type === 'message_deleted') {
        setMessages(prev => prev.map(m => m.message_id === data.message_id
          ? { ...m, deleted: true, text: '', image_url: null, voice_url: null, video_url: null, reactions: {} }
          : m));
      } else if (data.type === 'call.invite') {
        // Incoming call — ignore our own echo.
        if (data.from_user_id !== user?.user_id) {
          setInviteId(data.invite_id);
          setCallMode(data.mode === 'voice' ? 'voice' : 'video');
          setCallState('incoming');
        }
      } else if (data.type === 'call.answer') {
        // Someone answered/rejected the invite we made.
        if (data.from_user_id !== user?.user_id && data.invite_id === inviteIdRef.current) {
          if (data.accept) {
            // Callee accepted — mint our own token and join.
            joinCallAsCaller();
          } else {
            // Callee declined — reset outgoing state.
            cleanupCallState();
            toast.info('Call declined');
          }
        }
      } else if (data.type === 'call.decline') {
        if (data.from_user_id !== user?.user_id && data.invite_id === inviteIdRef.current) {
          cleanupCallState();
          toast.info('Call declined');
        }
      } else if (data.type === 'call.hangup') {
        if (data.from_user_id !== user?.user_id) {
          cleanupCallState();
        }
      } else if (data.type === 'call.busy') {
        if (data.from_user_id !== user?.user_id && data.invite_id === inviteIdRef.current) {
          cleanupCallState();
          toast.info('User is on another call');
        }
      } else {
        setMessages(prev => prev.find(m => m.message_id === data.message_id) ? prev : [...prev, {
          message_id: data.message_id,
          sender_id: data.sender_id,
          text: data.text || '',
          type: data.msg_type || (data.voice_url ? 'voice' : 'text'),
          voice_url: data.voice_url,
          duration: data.duration,
          waveform: data.waveform,
          read_by: data.read_by,
          created_at: data.created_at,
        }]);
        // Send read receipt only for other user's messages
        if (data.sender_id !== user?.user_id && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'read' }));
        }
      }
    };
    ws.onclose = () => { wsRef.current = null; setTimeout(connectWS, 3000); };
    ws.onerror = () => ws.close();
    wsRef.current = ws;
  }, [matchId, user?.user_id]);

  const fetchIce = useCallback(async () => {
    setLoadingIce(true);
    try { const r = await api.get(`/chat/${matchId}/icebreakers`); setIcebreakers(r.data.icebreakers || []); } catch {}
    finally { setLoadingIce(false); }
  }, [matchId]);

  useEffect(() => {
    fetchChat(); fetchIce(); connectWS();
    api.get(`/connection-progress/${matchId}`).then(r => setProgress(r.data)).catch(() => {});
    // Date ideas are fetched on-demand when the user opens the AI Date Planner
    // dialog — no eager call here so free/premium users don't rack up 403s.
    api.get('/calls/status').then(r => setCallEnabled(!!r.data.enabled)).catch(() => {});
    return () => { if (wsRef.current) wsRef.current.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, otherTyping]);

  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: true }));
      clearTimeout(typingTimeout.current);
      typingTimeout.current = setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: false }));
      }, 2000);
    }
  };

  const sendMsg = async (t, imageUrl = null) => {
    const msg = t || text.trim();
    if (!msg && !imageUrl) return;
    if (sending) return;
    setSending(true); if (!imageUrl) setText('');

    // Optimistic UI: show the message immediately
    const tempId = `tmp_${Date.now()}`;
    const optimistic = {
      message_id: tempId,
      sender_id: user?.user_id,
      text: msg,
      image_url: imageUrl,
      message_type: imageUrl ? 'image' : 'text',
      read_by: [user?.user_id],
      created_at: new Date().toISOString(),
      pending: true,
    };
    setMessages(prev => [...prev, optimistic]);

    try {
      const payload = { text: msg };
      if (imageUrl) payload.image_url = imageUrl;
      const r = await api.post(`/chat/${matchId}/messages`, payload);
      setMessages(prev => prev.map(m => m.message_id === tempId ? {
        message_id: r.data.message_id,
        sender_id: r.data.sender_id || user?.user_id,
        text: r.data.text || msg,
        image_url: r.data.image_url || imageUrl,
        message_type: r.data.message_type || (imageUrl ? 'image' : 'text'),
        read_by: r.data.read_by || [user?.user_id],
        created_at: r.data.created_at,
      } : m));
    } catch (e) {
      setMessages(prev => prev.filter(m => m.message_id !== tempId));
      if (!imageUrl) setText(msg);
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const sendImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('purpose', 'chat_image');
    setSending(true);
    try {
      const up = await api.post('/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = up.data?.url || up.data?.file_url || up.data?.path;
      if (!url) throw new Error('Upload returned no URL');
      await sendMsg('', url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Image upload failed');
    } finally {
      setSending(false);
    }
  };

  const block = async () => { try { await api.post(`/block/${otherUser?.user_id}`); navigate('/matches', { replace: true }); } catch {} };
  const reportUser = async (reason) => { try { await api.post(`/report/${otherUser?.user_id}`, { reason, description: '' }); toast.success('Reported'); setReportOpen(false); } catch {} };

  // Feb 27 2026 · Unmatch (without blocking). Server deletes the match doc,
  // messages, and any pending date proposals. Silent-unmatch credibility hit
  // applies if the caller is walking away from an unanswered incoming message.
  const unmatch = async () => {
    if (!window.confirm(`Unmatch ${otherUser?.display_name || 'this person'}? This clears the conversation for both of you. This cannot be undone.`)) return;
    try {
      await api.delete(`/matches/${matchId}`);
      toast.success('Unmatched');
      navigate('/matches', { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not unmatch');
    }
  };

  // Clear MY view of chat history. The other user's copy is untouched.
  const clearHistory = async () => {
    if (!window.confirm('Clear your view of this chat? The other person still sees their copy. This cannot be undone.')) return;
    try {
      await api.delete(`/chat/${matchId}/messages`);
      setMessages([]);
      toast.success('Chat cleared for you');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not clear');
    }
  };

  // Soft-delete a single message the CURRENT USER sent, with 5-second Undo grace.
  // Optimistic client-side tombstone appears immediately; the actual DELETE API
  // call is deferred by 5s. If the user taps "Undo" during that window we
  // cancel the timer and restore the message locally — no network round-trip.
  // The other user only sees the tombstone at t=5s (after the deferred DELETE
  // fires and broadcasts the `message_deleted` WS frame).
  const pendingDeletesRef = useRef({});
  const deleteMessage = (messageId) => {
    if (!messageId) return;
    const snap = messages.find(m => m.message_id === messageId);
    if (!snap || snap.deleted) return;
    // Optimistic tombstone (viewer only — no WS yet).
    setMessages(prev => prev.map(m => m.message_id === messageId
      ? { ...m, _pre_delete_snapshot: snap, _pending_delete: true,
          deleted: true, text: '', image_url: null, voice_url: null, video_url: null, reactions: {} }
      : m));
    // Schedule the real API delete in 5s.
    const timerId = setTimeout(async () => {
      delete pendingDeletesRef.current[messageId];
      try {
        await api.delete(`/chat/${matchId}/messages/${messageId}`);
      } catch (e) {
        // Roll back the tombstone if the server rejects.
        toast.error(e.response?.data?.detail || 'Could not delete');
        setMessages(prev => prev.map(m => (m.message_id === messageId && m._pre_delete_snapshot)
          ? { ...m._pre_delete_snapshot, _pending_delete: false, _pre_delete_snapshot: undefined }
          : m));
      }
    }, 5000);
    pendingDeletesRef.current[messageId] = timerId;
    toast('Message deleted', {
      duration: 5000,
      action: {
        label: 'Undo',
        onClick: () => {
          const t = pendingDeletesRef.current[messageId];
          if (t) { clearTimeout(t); delete pendingDeletesRef.current[messageId]; }
          setMessages(prev => prev.map(m => (m.message_id === messageId && m._pre_delete_snapshot)
            ? { ...m._pre_delete_snapshot, _pending_delete: false, _pre_delete_snapshot: undefined }
            : m));
        },
      },
    });
  };
  // Flush any pending soft-deletes on unmount so users can't cancel a delete
  // by navigating away from the chat during the 5s undo window.
  useEffect(() => () => {
    Object.entries(pendingDeletesRef.current).forEach(([mid, t]) => {
      clearTimeout(t);
      // Fire-and-forget. Best-effort — the axios call may not complete if the
      // page is truly unloading, but React unmounts (navigation within the SPA)
      // still have a live axios context, so the DELETE lands normally.
      api.delete(`/chat/${matchId}/messages/${mid}`).catch(() => {});
    });
    pendingDeletesRef.current = {};
  }, [matchId]);

  const politelyPass = async () => {
    if (!window.confirm("Send a polite 'not feeling a match' message and close this chat? No ghost penalty for either side.")) return;
    try {
      await api.post(`/chat/${matchId}/politely-pass`);
      toast.success("Sent — chat closed. +2 to your credibility score.");
      navigate('/matches', { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send polite pass');
    }
  };

  const sendVoice = async ({ blob, duration, peaks, mime }) => {
    const ext = (mime || '').includes('mp4') ? 'mp4'
      : (mime || '').includes('mpeg') ? 'mp3'
      : (mime || '').includes('ogg') ? 'ogg'
      : 'webm';
    const form = new FormData();
    form.append('file', new File([blob], `voice.${ext}`, { type: mime || 'audio/webm' }));

    const tempId = `tmp_${Date.now()}`;
    const localUrl = URL.createObjectURL(blob);
    const optimistic = {
      message_id: tempId,
      sender_id: user?.user_id,
      type: 'voice',
      text: '',
      voice_url: null,
      local_src: localUrl,
      duration,
      waveform: peaks,
      read_by: [user?.user_id],
      created_at: new Date().toISOString(),
      pending: true,
    };
    setMessages(prev => [...prev, optimistic]);

    try {
      const params = new URLSearchParams({ duration: String(duration), waveform: JSON.stringify(peaks) });
      const r = await api.post(`/chat/${matchId}/voice?${params.toString()}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessages(prev => prev.map(m => m.message_id === tempId ? {
        message_id: r.data.message_id,
        sender_id: r.data.sender_id,
        type: 'voice',
        text: '',
        voice_url: r.data.voice_url,
        duration: r.data.duration,
        waveform: r.data.waveform,
        read_by: r.data.read_by || [user?.user_id],
        created_at: r.data.created_at,
      } : m));
    } catch (e) {
      setMessages(prev => prev.filter(m => m.message_id !== tempId));
      toast.error(e.response?.data?.detail || 'Failed to send voice message');
    }
  };

  const react = async (messageId, emoji) => {
    // Reactions are available to ALL tiers (Feb 2026 chat-polish sprint).
    // Do NOT re-add a tier check — backend accepts reactions from any authed match member.
    setReactionMsgId(null);
    try {
      const r = await api.post(`/chat/${matchId}/messages/${messageId}/react`, { emoji });
      setMessages(prev => prev.map(m => m.message_id === messageId ? { ...m, reactions: r.data.reactions } : m));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const cleanupCallState = () => {
    setCallState(null);
    setInviteId(null);
    setCallToken(null);
    setCallRoomName(null);
    if (outgoingTimeoutRef.current) {
      clearTimeout(outgoingTimeoutRef.current);
      outgoingTimeoutRef.current = null;
    }
  };

  const startOutgoingCall = (mode) => {
    if (!callEnabled) { toast.error('Calling is not configured yet'); return; }
    if (callState) return;
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      toast.error('Reconnecting… try again in a moment');
      return;
    }
    const id = `inv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    setInviteId(id);
    setCallMode(mode);
    setCallState('outgoing');
    wsRef.current.send(JSON.stringify({ type: 'call.invite', invite_id: id, mode }));
    api.post('/calls/event', { match_id: matchId, invite_id: id, event: 'invite', mode }).catch(() => {});
    // Auto-cancel if no answer in 40s (mark missed).
    outgoingTimeoutRef.current = setTimeout(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'call.hangup', invite_id: id, mode }));
      }
      api.post('/calls/event', { match_id: matchId, invite_id: id, event: 'missed', mode }).catch(() => {});
      cleanupCallState();
      toast.info('No answer');
    }, 40000);
  };

  const acceptIncomingCall = async () => {
    if (!inviteId) return;
    try {
      const r = await api.post('/calls/token', { match_id: matchId, mode: callMode });
      setCallToken(r.data.token);
      setCallRoomName(r.data.room_name);
      setCallState('live');
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'call.answer', invite_id: inviteId, accept: true, mode: callMode }));
      }
      api.post('/calls/event', { match_id: matchId, invite_id: inviteId, event: 'accept', mode: callMode }).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not join call');
      cleanupCallState();
    }
  };

  const declineIncomingCall = () => {
    if (!inviteId) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'call.answer', invite_id: inviteId, accept: false, mode: callMode }));
    }
    api.post('/calls/event', { match_id: matchId, invite_id: inviteId, event: 'decline', mode: callMode }).catch(() => {});
    cleanupCallState();
  };

  const joinCallAsCaller = async () => {
    // Callee accepted our invite — mint OUR token now (deferred until accept
    // so we don't request Twilio quota for calls that never connect).
    if (outgoingTimeoutRef.current) {
      clearTimeout(outgoingTimeoutRef.current);
      outgoingTimeoutRef.current = null;
    }
    try {
      const r = await api.post('/calls/token', { match_id: matchId, mode: callMode });
      setCallToken(r.data.token);
      setCallRoomName(r.data.room_name);
      setCallState('live');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not join call');
      cleanupCallState();
    }
  };

  const endCall = () => {
    if (inviteIdRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'call.hangup', invite_id: inviteIdRef.current, mode: callMode,
      }));
    }
    if (inviteIdRef.current) {
      api.post('/calls/event', {
        match_id: matchId, invite_id: inviteIdRef.current, event: 'hangup', mode: callMode,
      }).catch(() => {});
    }
    cleanupCallState();
  };

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#EF4444]" /></div>;

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="chat-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-black/[0.06] px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/matches')} data-testid="chat-back" className="text-zinc-500 hover:text-white"><ArrowLeft size={20}/></button>
          <button
            onClick={() => otherUser?.user_id && navigate(`/user/${otherUser.user_id}`)}
            disabled={!otherUser?.user_id || otherUser?.is_deleted}
            data-testid="chat-view-profile-btn"
            aria-label={`View ${otherUser?.display_name || 'match'}'s profile`}
            className="w-10 h-10 rounded-full overflow-hidden bg-[#F8F9FA] ring-1 ring-white/[0.06] hover:ring-[#EF4444]/40 transition-all disabled:cursor-not-allowed disabled:opacity-60">
            <img src={getPhotoUrl(otherUser?.photo_url) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${otherUser?.user_id}`}
              alt="" className="w-full h-full object-cover" />
          </button>
          <div className="flex-1 min-w-0">
            <button
              onClick={() => otherUser?.user_id && !otherUser?.is_deleted && navigate(`/user/${otherUser.user_id}`)}
              disabled={!otherUser?.user_id || otherUser?.is_deleted}
              data-testid="chat-view-profile-name"
              className="flex items-center gap-1.5 min-w-0 text-left hover:opacity-80 transition-opacity disabled:cursor-not-allowed">
              <p className="font-semibold text-sm truncate" data-testid="chat-other-name">{otherUser?.display_name}</p>
              <VerifiedBadge badges={otherUser?.badges} size={14} data-testid="chat-verified-checkmark" />
              {otherUser?.is_seed && <DemoProfileBadge isSeed={true} size="xs" />}
              {lifecycleCtl.pill}
            </button>{otherTyping ? (
              <p className="text-[11px] text-[#EF4444] flex items-center gap-1">
                typing<span className="flex gap-0.5 ml-0.5"><span className="typing-dot animate-typing-1"/><span className="typing-dot animate-typing-2"/><span className="typing-dot animate-typing-3"/></span>
              </p>
            ) : matchData?.compatibility_score ? (
              <button
                data-testid="chat-compat-open-btn"
                onClick={() => {
                  setCompatOpen(true);
                  if (!compatData && otherUser?.user_id) {
                    setCompatLoading(true);
                    api.get(`/v2/scores/compatibility/${otherUser.user_id}`)
                      .then(r => setCompatData(r.data))
                      .catch(() => {})
                      .finally(() => setCompatLoading(false));
                  }
                }}
                className="text-[11px] text-zinc-500 hover:text-[#0F0F10] transition-colors underline decoration-dotted underline-offset-2">
                {matchData.compatibility_score}% match
              </button>
            ) : null}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="chat-menu" className="w-8 h-8 rounded-full hover:bg-black/[0.03] flex items-center justify-center"><MoreVertical size={16} className="text-zinc-500"/></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white border-black/[0.08] rounded-xl">
              {otherUser?.user_id && !otherUser?.is_deleted && (
                <DropdownMenuItem
                  onClick={() => navigate(`/user/${otherUser.user_id}`)}
                  data-testid="chat-view-profile-menu"
                  className="text-sm cursor-pointer text-zinc-600 focus:bg-black/[0.03] focus:text-zinc-900">
                  <Heart size={14} className="mr-2 text-[#EF4444]"/> View profile
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => navigate('/ai-coach?starter=chat_reply_help')}
                data-testid="chat-ai-assistant-btn"
                className="text-sm cursor-pointer text-zinc-600 focus:bg-black/[0.03] focus:text-zinc-900"
              >
                <Wand2 size={14} className="mr-2 text-[#7C3AED]"/> AI Assistant
              </DropdownMenuItem>
              {callEnabled && (
                <DropdownMenuItem onClick={() => setHistoryOpen(true)} data-testid="chat-call-history-btn" className="text-sm cursor-pointer text-zinc-600 focus:bg-black/[0.03] focus:text-zinc-900">
                  <History size={14} className="mr-2 text-zinc-500"/> Call history
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={politelyPass} data-testid="chat-polite-pass-btn" className="text-sm cursor-pointer text-zinc-600 focus:bg-black/[0.03] focus:text-zinc-900">
                <Heart size={14} className="mr-2 text-amber-500"/> Politely Pass
              </DropdownMenuItem>
              <DropdownMenuItem onClick={clearHistory} data-testid="chat-clear-history-btn" className="text-sm cursor-pointer text-zinc-600 focus:bg-black/[0.03] focus:text-zinc-900">
                <Eraser size={14} className="mr-2 text-zinc-500"/> Clear chat history
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setReportOpen(true)} data-testid="chat-report-btn" className="text-sm cursor-pointer text-zinc-600 focus:bg-black/[0.03] focus:text-white">
                <Flag size={14} className="mr-2 text-amber-600"/> Report
              </DropdownMenuItem>
              <DropdownMenuItem onClick={unmatch} data-testid="chat-unmatch-btn" className="text-sm text-red-500 cursor-pointer focus:bg-red-500/10 focus:text-red-600">
                <UserX size={14} className="mr-2"/> Unmatch
              </DropdownMenuItem>
              <DropdownMenuItem onClick={block} data-testid="chat-block-btn" className="text-sm text-red-500 cursor-pointer focus:bg-red-500/10 focus:text-red-600">
                <ShieldBan size={14} className="mr-2"/> Block
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {callEnabled && (
            <>
              <button
                onClick={() => startOutgoingCall('voice')}
                disabled={!!callState}
                data-testid="chat-voice-call-btn"
                aria-label="Start voice call"
                className="w-8 h-8 rounded-full hover:bg-black/[0.03] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
                <Phone size={16} strokeWidth={2} className="text-zinc-500 hover:text-[#0F0F10]"/>
              </button>
              <button
                onClick={() => startOutgoingCall('video')}
                disabled={!!callState}
                data-testid="chat-video-call-btn"
                aria-label="Start video call"
                className="w-8 h-8 rounded-full hover:bg-black/[0.03] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
                <Video size={16} strokeWidth={2} className="text-zinc-500 hover:text-[#0F0F10]"/>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Connection Progress Bar — icebreaker milestones (getting-to-know tracker). */}
      {progress && (
        <div className="px-4 py-2 border-b border-black/[0.06] bg-white" data-testid="connection-progress">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] text-zinc-500 font-semibold uppercase tracking-wider">Connection Progress</span>
            <div className="flex items-center gap-1.5">
              <button onClick={openDatePlanner} className="text-[10px] text-[#0F0F10] font-semibold flex items-center gap-1 hover:opacity-70 transition-opacity" data-testid="show-date-ideas">
                <Sparkles size={10} className="text-[#D9A748]"/> AI Date Planner
              </button>
              <span className="text-[10px] text-[#EF4444] font-bold">{progress.progress}%</span>
            </div>
          </div>
          <div className="flex gap-1">
            {progress.milestones.map(m => (
              <div key={m.id} className="flex-1" title={m.description}>
                <div className={`h-1.5 rounded-full ${progress.completed.includes(m.id) ? 'brand-gradient' : 'bg-black/[0.03]'}`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2" data-testid="messages-list">
        {messages.length === 0 && <div className="text-center py-20"><p className="text-zinc-600 text-sm">No messages yet. Break the ice!</p></div>}
        {messages.map((msg, mi) => {
          // Feb 2026 · Match → Meet → Keep persistent lifecycle cards. Server
          // inserts these with sender_id="system" and kind="lifecycle_{milestone}"
          // when a mutual milestone fires, so the moment stays permanently in
          // the chat scrollback.
          if (msg.kind === 'lifecycle_meet' || msg.kind === 'lifecycle_met' || msg.kind === 'lifecycle_keep'
              || msg.kind === 'date_proposal' || msg.kind === 'date_proposal_counter'
              || msg.kind === 'date_proposal_accepted' || msg.kind === 'date_proposal_declined'
              || msg.kind === 'date_cancelled' || msg.kind === 'date_proposal_withdrawn') {
            // Some cards are addressed to one user only (e.g., decline —
            // sender-only visibility). Server tags them via `meta.visible_to`.
            const vt = msg.meta?.visible_to;
            if (vt && vt !== user?.user_id) return null;
            const cardMeta = {
              lifecycle_meet:            { icon: MapPin,        color: '#15803D', label: '🎉 It\u2019s a Meet!',  sub: 'You both want to meet. Time to make it real.' },
              lifecycle_met:             { icon: Check,          color: '#1E4FD8', label: '✓ Date confirmed',    sub: 'You both confirmed you met.' },
              lifecycle_keep:            { icon: Heart,          color: '#B91C1C', label: '❤️ It\u2019s a Keep!',  sub: 'You both want to keep seeing each other.' },
              date_proposal:             { icon: MapPin,        color: '#B38138', label: '📅 Date proposal',      sub: msg.text?.replace('📅 ', '') || 'A new date has been proposed.' },
              date_proposal_counter:     { icon: MapPin,        color: '#B38138', label: '↩️ Counter-proposal',   sub: msg.text?.replace(/^[↩️📅]\s*/, '') || 'A different date was suggested.' },
              date_proposal_accepted:    { icon: Check,          color: '#15803D', label: '🎉 It\u2019s Planned!', sub: 'You both agreed on the date.' },
              date_proposal_declined:    { icon: Heart,          color: '#8A8A93', label: 'Date declined',         sub: msg.text || 'Your proposal was declined.' },
              date_cancelled:            { icon: Heart,          color: '#8A8A93', label: 'Date cancelled',        sub: msg.text || 'The confirmed date was cancelled.' },
              date_proposal_withdrawn:   { icon: Heart,          color: '#8A8A93', label: 'Proposal withdrawn',    sub: msg.text || 'The proposal was withdrawn.' },
            }[msg.kind];
            const Icon = cardMeta.icon;
            return (
              <div key={msg.message_id || mi} className="flex justify-center py-3"
                   data-testid={`lifecycle-card-${msg.kind}`}>
                <div className="max-w-[340px] w-full rounded-2xl border p-4 flex items-start gap-3"
                     style={{ borderColor: `${cardMeta.color}33`, backgroundColor: `${cardMeta.color}0a` }}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                       style={{ backgroundColor: `${cardMeta.color}22` }}>
                    <Icon size={18} strokeWidth={2} style={{ color: cardMeta.color }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-[13px] font-bold tracking-tight" style={{ color: cardMeta.color }}>
                      {cardMeta.label}
                    </p>
                    <p className="text-[12px] text-[#4B4B52] mt-0.5 leading-snug">{cardMeta.sub}</p>
                    <p className="text-[10px] text-[#8A8A93] mt-1.5 uppercase tracking-wider font-semibold">
                      {new Date(msg.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                </div>
              </div>
            );
          }
          const isMe = msg.sender_id === user?.user_id;
          const isPending = !!msg.pending;
          const isDeleted = !!msg.deleted;
          const isRead = isMe && msg.read_by && msg.read_by.length > 1;
          const showDateDivider = mi === 0 || !isSameDay(messages[mi - 1]?.created_at, msg.created_at);
          const isDelivered = isMe && !isPending && !isRead;
          const isLast = mi === messages.length - 1 || messages[mi+1]?.sender_id !== msg.sender_id;
          const reactionCounts = {};
          Object.values(msg.reactions || {}).forEach(e => { reactionCounts[e] = (reactionCounts[e] || 0) + 1; });
          const readAt = isRead && msg.read_at && otherUser?.user_id ? msg.read_at[otherUser.user_id] : null;
          if (isDeleted) {
            return (
              <Fragment key={msg.message_id || mi}>
                {showDateDivider && (
                  <div className="flex justify-center py-3" data-testid={`date-divider-${mi}`}>
                    <span className="text-[11px] font-semibold tracking-wide text-[#8A8A93] uppercase bg-white px-3">
                      {formatDateDivider(msg.created_at)}
                    </span>
                  </div>
                )}
                <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                  <div
                    data-testid={`message-deleted-${msg.message_id}`}
                    className="px-4 py-2 rounded-2xl bg-black/[0.04] border border-black/[0.06] text-[12px] italic text-zinc-500 flex items-center gap-1.5">
                    <Trash2 size={12} strokeWidth={2} />
                    {isMe ? 'You deleted this message' : 'This message was deleted'}
                  </div>
                </div>
              </Fragment>
            );
          }
          return (
            <Fragment key={msg.message_id || mi}>
              {showDateDivider && (
                <div className="flex justify-center py-3" data-testid={`date-divider-${mi}`}>
                  <span className="text-[11px] font-semibold tracking-wide text-[#8A8A93] uppercase bg-white px-3">
                    {formatDateDivider(msg.created_at)}
                  </span>
                </div>
              )}
              <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div className="relative group">
                <div
                  className={`message-bubble px-4 py-2.5 ${
                    isMe
                      ? `bg-gradient-to-br from-[#3B82F6] to-[#1E40AF] text-white rounded-2xl rounded-br-md shadow-[0_2px_8px_-2px_rgba(59,130,246,0.35)] ${isPending ? 'opacity-75' : ''}`
                      : 'brand-gradient text-white rounded-2xl rounded-bl-md shadow-[0_2px_8px_-2px_rgba(185,28,28,0.3)]'
                  }`}
                  data-testid={`message-${msg.message_id || mi}`}
                  onContextMenu={(e) => { e.preventDefault(); if (msg.message_id && !msg.pending) setReactionMsgId(msg.message_id); }}
                  onDoubleClick={() => { if (msg.message_id && !msg.pending) setReactionMsgId(msg.message_id); }}
                >
                  {msg.type === 'voice' ? (
                    <VoiceBubble message={msg} matchId={matchId} isMe={isMe} />
                  ) : msg.image_url ? (
                    <div className="space-y-1" data-testid={`msg-image-${msg.message_id}`}>
                      <img
                        src={msg.image_url}
                        alt="shared"
                        onClick={() => window.open(msg.image_url, '_blank')}
                        className="rounded-lg max-w-[240px] max-h-[320px] object-cover cursor-pointer"
                        style={{ opacity: msg.pending ? 0.5 : 1 }}
                      />
                      {msg.text ? <p className="text-sm leading-relaxed">{msg.text}</p> : null}
                    </div>
                  ) : (
                    <p className="text-sm leading-relaxed">{msg.text}</p>
                  )}
                  <div className={`flex items-center gap-1 mt-1 ${isMe ? 'justify-end' : ''}`}>
                    <span className="text-[10px] text-white/75 tabular-nums">
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {isMe && isLast && (
                      isPending
                        ? <span title="Sending"><Clock size={11} className="text-white/60" data-testid="status-pending" /></span>
                        : isRead
                          ? <span title={readAt && myTier === 'vip' ? `Read at ${new Date(readAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Read'}>
                              <CheckCheck size={12} className="text-white" data-testid="status-read" />
                            </span>
                          : isDelivered
                            ? <span title="Delivered"><Check size={12} className="text-white/75" data-testid="status-delivered" /></span>
                            : null
                    )}
                  </div>
                </div>
                {/* Reactions row */}
                {Object.keys(reactionCounts).length > 0 && (
                  <div className={`flex gap-1 mt-1 ${isMe ? 'justify-end' : ''}`}>
                    {Object.entries(reactionCounts).map(([e,c]) => (
                      <button key={e} onClick={() => react(msg.message_id, e)}
                        className="bg-white border border-black/[0.08] rounded-full px-1.5 py-0.5 text-[11px] hover:bg-black/[0.02] shadow-sm" data-testid={`reaction-${msg.message_id}-${e}`}>
                        {e} {c > 1 && <span className="text-[9px] text-zinc-500 ml-0.5">{c}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {/* VIP read timestamp below last read message */}
                {isMe && isLast && isRead && readAt && myTier === 'vip' && (
                  <p className="text-[9px] text-zinc-500 mt-1 text-right" data-testid="read-time">
                    Read {new Date(readAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
                {/* Reaction picker popover */}
                {reactionMsgId === msg.message_id && (
                  <div className={`absolute -top-10 ${isMe ? 'right-0' : 'left-0'} z-20 bg-white rounded-full shadow-xl border border-black/[0.08] px-2 py-1.5 flex gap-1 animate-scale-in`}
                    data-testid={`reaction-picker-${msg.message_id}`} onMouseLeave={() => setReactionMsgId(null)}>
                    {['❤️','🔥','😂','😮','😢','👏'].map(e => (
                      <button key={e} onClick={() => react(msg.message_id, e)}
                        className="text-xl hover:scale-125 transition-transform p-0.5" data-testid={`pick-${e}`}>{e}</button>
                    ))}
                  </div>
                )}
                {/* Long-press hint button on hover (desktop) */}
                {!msg.pending && msg.message_id && (
                  <button onClick={() => setReactionMsgId(reactionMsgId === msg.message_id ? null : msg.message_id)}
                    className={`absolute top-1/2 -translate-y-1/2 ${isMe ? '-left-7' : '-right-7'} w-6 h-6 rounded-full bg-white border border-black/[0.08] text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-black/[0.02]`}
                    data-testid={`react-trigger-${msg.message_id}`} aria-label="React">+</button>
                )}
                {/* Delete-my-message button (own messages only, on hover) */}
                {isMe && !msg.pending && msg.message_id && (
                  <button onClick={() => deleteMessage(msg.message_id)}
                    className="absolute top-1/2 -translate-y-1/2 -left-[3.5rem] w-6 h-6 rounded-full bg-white border border-black/[0.08] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-500/10"
                    data-testid={`delete-msg-${msg.message_id}`} aria-label="Delete message">
                    <Trash2 size={12} className="text-red-500" strokeWidth={2.2} />
                  </button>
                )}
              </div>
            </div>
            </Fragment>
          );
        })}
        {/* Typing indicator */}
        {otherTyping && (
          <div className="flex justify-start">
            <div className="bg-[#F8F9FA] border border-black/[0.06] rounded-2xl rounded-bl-md px-5 py-3 flex gap-1.5" data-testid="typing-indicator">
              <span className="typing-dot animate-typing-1"/><span className="typing-dot animate-typing-2"/><span className="typing-dot animate-typing-3"/>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Icebreakers */}
      {icebreakers.length > 0 && messages.length < 3 && (
        <div className="px-4 py-2.5 border-t border-black/[0.06] bg-white">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-[#EF4444]"/>
            <span className="text-[10px] text-zinc-500 font-semibold uppercase tracking-wider">AI Icebreakers</span>
            {loadingIce && <Loader2 className="w-3 h-3 animate-spin text-[#EF4444]"/>}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {icebreakers.map((ice,i) => <button key={i} onClick={() => sendMsg(ice)} data-testid={`icebreaker-${i}`} className="icebreaker-chip flex-shrink-0">{ice}</button>)}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur-xl border-t border-black/[0.06] px-4 py-3">
        <form onSubmit={e => { e.preventDefault(); sendMsg(); }} className="flex gap-2 items-center">
          <VoiceRecorder onSend={sendVoice} disabled={sending} />
          {/* Image share (Feb 2026 chat-polish sprint). Same 5MB / JPG,PNG,WEBP
              limits as profile photo uploads. Uses hidden file input +
              paperclip trigger so the UI reads clean on mobile. */}
          <input
            data-testid="chat-image-picker"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ display: 'none' }}
            ref={(el) => (window.__chatImageInputRef = el)}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) sendImage(f);
              e.target.value = '';
            }}
          />
          <button
            data-testid="chat-image-btn"
            type="button"
            onClick={() => window.__chatImageInputRef?.click()}
            disabled={sending}
            className="w-9 h-9 rounded-full flex items-center justify-center text-zinc-500 hover:text-[#0F0F10] hover:bg-black/[0.05] disabled:opacity-50 transition"
            title="Send photo">
            <Paperclip size={16} />
          </button>
          <Input data-testid="chat-input" placeholder="Type a message..."
            className="flex-1 rounded-xl bg-[#F5F5F5] border-black/[0.08] text-[#0F0F10] placeholder:text-[#8A8A93] focus-visible:ring-[#B91C1C]/30 focus-visible:border-[#B91C1C]/30 px-4 py-3"
            value={text} onChange={e => { setText(e.target.value); handleTyping(); }} />
          <button type="submit" disabled={!text.trim() || sending} data-testid="send-message-btn"
            className="w-12 h-12 rounded-xl brand-gradient flex items-center justify-center transition-all active:scale-90 disabled:opacity-30 text-white">
            {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send size={16}/>}
          </button>
        </form>
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="bg-white border-black/[0.08] rounded-2xl p-6 max-w-sm" data-testid="chat-report-dialog">
          <DialogTitle className="font-bold mb-4">Report User</DialogTitle>
          <div className="space-y-2">
            {['Inappropriate content','Fake profile','Harassment','Spam','Other'].map(r => (
              <button key={r} onClick={() => reportUser(r)} className="w-full text-left px-4 py-3 rounded-xl border border-black/[0.06] hover:bg-black/[0.02] text-sm">{r}</button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Date Planner Dialog — VIP-only. 403 → upgrade CTA. */}
      <Dialog open={showIdeas} onOpenChange={setShowIdeas}>
        <DialogContent className="bg-white border-black/[0.08] rounded-2xl p-6 max-w-sm max-h-[75vh] overflow-y-auto" data-testid="date-ideas-dialog">
          <div className="flex items-start justify-between gap-2 mb-1">
            <DialogTitle className="font-[800] tracking-[-0.4px] flex items-center gap-2 text-[18px]">
              <Sparkles size={18} className="text-[#D9A748]"/> AI Date Planner
            </DialogTitle>
            {!dateIdeasVipGate && (
              <button
                onClick={refreshDateIdeas}
                disabled={dateIdeasLoading}
                data-testid="date-planner-refresh-btn"
                aria-label="Refresh ideas"
                className="w-8 h-8 rounded-full flex items-center justify-center text-[#6B7280] hover:text-[#0F0F10] hover:bg-black/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Fresh ideas"
              >
                <RefreshCw size={15} className={dateIdeasLoading ? 'animate-spin' : ''} />
              </button>
            )}
          </div>
          <p className="text-[12px] text-zinc-500 mb-4 leading-snug">
            Personalized date ideas based on both of your interests, values and lifestyle.
          </p>
          {dateIdeasVipGate ? (
            <div className="text-center py-6" data-testid="date-planner-vip-gate">
              <div className="w-14 h-14 rounded-full bg-[#F4F5F8] flex items-center justify-center mx-auto mb-3">
                <Sparkles size={22} className="text-[#D9A748]"/>
              </div>
              <h4 className="text-[16px] font-[800] text-[#0F0F10] mb-1.5">A VIP-only perk</h4>
              <p className="text-[13px] text-zinc-500 leading-relaxed mb-5 px-2">
                AI Date Planner reads both profiles and tailors ideas to what you actually share.
                Upgrade to VIP to unlock it.
              </p>
              <button
                onClick={() => { setShowIdeas(false); navigate('/subscription'); }}
                data-testid="date-planner-upgrade-btn"
                className="w-full py-3 rounded-full bg-[#0F0F10] text-white text-[14px] font-[800] tracking-[-0.2px] hover:bg-[#1F1F22] transition-colors"
              >
                Upgrade to VIP
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {dateIdeasLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={22} className="animate-spin text-[#6B7280]"/>
                </div>
              )}
              {!dateIdeasLoading && dateIdeas.map((idea, i) => {
                const isSaved = savedIdeaTitles.has(idea.title);
                return (
                <div key={i} className="bg-white border border-black/[0.06] rounded-2xl p-4 relative" data-testid={`date-idea-${i}`}>
                  <button
                    onClick={() => toggleSaveIdea(idea)}
                    data-testid={`date-idea-save-${i}`}
                    aria-label={isSaved ? 'Remove from saved' : 'Save idea'}
                    className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-black/[0.04]"
                  >
                    {isSaved ? <BookmarkCheck size={16} className="text-[#0F0F10]" /> : <Bookmark size={16} className="text-[#6B7280]" />}
                  </button>
                  <div className="flex items-center gap-2 mb-1.5 pr-8">
                    <span className="text-[10px] uppercase tracking-[0.6px] text-zinc-500 font-[800]">{idea.category}</span>
                  </div>
                  <h4 className="font-[800] text-[15px] text-[#0F0F10] tracking-[-0.2px] mb-1 pr-8">{idea.title}</h4>
                  <p className="text-[13px] text-zinc-600 leading-snug">{idea.description}</p>
                </div>
              );})}
              {!dateIdeasLoading && dateIdeas.length === 0 && !dateIdeasVipGate && (
                <p className="text-sm text-zinc-500 text-center py-4">No ideas yet — try again in a moment.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Incoming call dialog */}
      {callState === 'incoming' && (
        <Dialog open onOpenChange={(v) => { if (!v) declineIncomingCall(); }}>
          <DialogContent className="bg-white border-black/[0.08] rounded-2xl p-6 max-w-sm text-center" data-testid="incoming-call-dialog">
            <div className="flex flex-col items-center gap-4">
              <img
                src={getPhotoUrl(otherUser?.photo_url) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${otherUser?.user_id}`}
                alt=""
                className="w-24 h-24 rounded-full object-cover ring-4 ring-[#EF4444]/20"
              />
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
                  Incoming {callMode === 'voice' ? 'voice' : 'video'} call
                </p>
                <DialogTitle className="font-brand text-2xl mt-1">{otherUser?.display_name}</DialogTitle>
              </div>
              <div className="flex gap-4 mt-2">
                <button
                  onClick={declineIncomingCall}
                  data-testid="incoming-call-decline"
                  className="w-16 h-16 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white flex items-center justify-center transition-all active:scale-95">
                  <PhoneOff size={22} strokeWidth={2.2}/>
                </button>
                <button
                  onClick={acceptIncomingCall}
                  data-testid="incoming-call-accept"
                  className="w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center transition-all active:scale-95">
                  {callMode === 'voice' ? <Phone size={22} strokeWidth={2.2}/> : <Video size={22} strokeWidth={2.2}/>}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Outgoing "ringing" overlay while waiting for callee to accept */}
      {callState === 'outgoing' && (
        <div className="fixed inset-0 z-[95] bg-[#0F0F10]/95 backdrop-blur-md flex flex-col items-center justify-center gap-6 text-white" data-testid="outgoing-call-overlay">
          <img
            src={getPhotoUrl(otherUser?.photo_url) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${otherUser?.user_id}`}
            alt=""
            className="w-28 h-28 rounded-full object-cover ring-4 ring-white/10"
          />
          <div className="text-center">
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/60 font-semibold">
              Calling…
            </p>
            <p className="text-2xl font-semibold mt-1">{otherUser?.display_name}</p>
            <p className="text-white/60 text-sm mt-2">
              {callMode === 'voice' ? 'Voice call' : 'Video call'}
            </p>
          </div>
          <Loader2 className="w-6 h-6 animate-spin text-white/60" />
          <button
            onClick={endCall}
            data-testid="outgoing-call-cancel"
            className="w-16 h-16 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white flex items-center justify-center transition-all active:scale-95">
            <PhoneOff size={22} strokeWidth={2.2}/>
          </button>
        </div>
      )}

      {/* Live call modal */}
      {callState === 'live' && callToken && callRoomName && (
        <CallModal
          token={callToken}
          roomName={callRoomName}
          mode={callMode}
          otherName={otherUser?.display_name}
          otherPhoto={getPhotoUrl(otherUser?.photo_url)}
          onEnd={endCall}
        />
      )}

      {/* Per-match call history — reachable via the chat menu's "Call history" item */}
      <CallHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        matchId={matchId}
        otherName={otherUser?.display_name}
        onCallBack={(mode) => startOutgoingCall(mode)}
      />

      {/* Compatibility drill-down — Scoring v2 (7-category breakdown). */}
      <Dialog open={compatOpen} onOpenChange={setCompatOpen}>
        <DialogContent className="max-w-md bg-white text-[#0F0F10] p-0 gap-0 rounded-2xl overflow-hidden max-h-[85vh] flex flex-col" data-testid="compat-dialog">
          <DialogTitle className="sr-only">Compatibility breakdown</DialogTitle>
          <div className="p-5 border-b border-[var(--hairline)]">
            <p className="text-[10.5px] uppercase tracking-[0.14em] text-[#8A8A93] font-semibold">Compatibility with</p>
            <p className="text-[15px] font-semibold mt-0.5">{otherUser?.display_name}</p>
          </div>
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {compatLoading && (
              <p className="text-[13px] text-[#8A8A93] text-center py-6" data-testid="compat-loading">Calculating…</p>
            )}
            {!compatLoading && compatData && (
              <>
                <div className="flex items-center gap-4 mb-2">
                  <div className="w-16 h-16 rounded-full bg-[#B91C1C]/10 flex items-center justify-center">
                    <p className="text-[26px] font-bold text-[#B91C1C] tabular-nums" data-testid="compat-score-big">{compatData.score}</p>
                  </div>
                  <div className="flex-1">
                    {compatData.dealbreaker_flags?.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5" data-testid="compat-dealbreakers">
                        {compatData.dealbreaker_flags.map(f => (
                          <span key={f} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#FEE2E2] text-[#B91C1C]">{f.replace(/_/g, ' ')}</span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11.5px] text-[#4B4B52]">No dealbreakers — this is a compatible pair.</p>
                    )}
                    {compatData.distance_km != null && (
                      <p className="text-[10.5px] text-[#8A8A93] mt-1">Distance: {compatData.distance_km} km</p>
                    )}
                  </div>
                </div>

                {Object.entries(compatData.breakdown || {}).map(([cat, val]) => {
                  const pts = val?.points ?? 0;
                  const weight = val?.weight ?? 0;
                  const pct = weight > 0 ? Math.min(100, (pts / weight) * 100) : 0;
                  return (
                    <div key={cat} data-testid={`compat-cat-${cat}`}>
                      <div className="flex justify-between items-baseline mb-1">
                        <p className="text-[12px] font-semibold">{cat.replace(/_/g, ' ')}</p>
                        <p className="text-[11px] tabular-nums text-[#4B4B52]">{Number(pts).toFixed(1)} / {weight}</p>
                      </div>
                      <div className="h-1.5 bg-[var(--hairline)] rounded-full overflow-hidden">
                        <div className="h-full brand-gradient rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
                <p className="text-[10.5px] text-[#8A8A93] pt-2">Scoring engine v{compatData.version}</p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {lifecycleCtl.sheet}
      {lifecycleCtl.celebration}
      <ProposalComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        matchId={matchId}
        otherName={otherUser?.display_name}
        mode={composerMode}
        initialProposal={composerInitial}
        onSubmitted={(data) => {
          if (data?.state) setLifecycle(data.state);
        }}
      />
      <ViewDate
        open={viewDateOpen}
        onClose={() => setViewDateOpen(false)}
        proposal={lifecycle?.current_proposal}
        otherName={otherUser?.display_name}
      />
    </div>
  );
}
